import apiClient from '../client';
import { portalClient } from '../portalClient';
import type {
  DocumentListItem,
  DocumentDetail,
  DocumentUploadResponse,
  DocumentStats,
  DocumentUpdateRequest,
  DocumentType,
  DocumentTypeInfo,
  VerifyExtractionRequest,
} from '@/types/document.types';
import type { PaginatedResponse, PaginationParams } from '@/api/types/pagination.types';

/**
 * API for document management (team dashboard).
 */
export const documentsApi = {
  /**
   * Upload a document.
   */
  upload: async (
    file: File,
    patientId: string,
    documentType: DocumentType,
    options?: {
      category?: string;
      title?: string;
      description?: string;
      documentDate?: string;
      taskId?: string;
      demandId?: string;
      isConfidential?: boolean;
    }
  ): Promise<DocumentUploadResponse> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('patientId', patientId);
    formData.append('documentType', documentType);

    if (options?.category) formData.append('category', options.category);
    if (options?.title) formData.append('title', options.title);
    if (options?.description) formData.append('description', options.description);
    if (options?.documentDate) formData.append('documentDate', options.documentDate);
    if (options?.taskId) formData.append('taskId', options.taskId);
    if (options?.demandId) formData.append('demandId', options.demandId);
    if (options?.isConfidential !== undefined) {
      formData.append('isConfidential', String(options.isConfidential));
    }

    const response = await apiClient.post<DocumentUploadResponse>('/v1/documents', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  /**
   * List documents for a patient with pagination.
   */
  getByPatient: async (
    patientId: string,
    params?: PaginationParams
  ): Promise<PaginatedResponse<DocumentListItem>> => {
    const response = await apiClient.get<PaginatedResponse<DocumentListItem>>(
      `/v1/documents/patient/${patientId}`,
      { params }
    );
    return response.data;
  },

  /**
   * List all documents for a patient (no pagination).
   */
  getAllByPatient: async (patientId: string): Promise<DocumentListItem[]> => {
    const response = await apiClient.get<DocumentListItem[]>(
      `/v1/documents/patient/${patientId}/all`
    );
    return response.data;
  },

  /**
   * List documents by type for a patient.
   */
  getByPatientAndType: async (
    patientId: string,
    type: DocumentType
  ): Promise<DocumentListItem[]> => {
    const response = await apiClient.get<DocumentListItem[]>(
      `/v1/documents/patient/${patientId}/type/${type}`
    );
    return response.data;
  },

  /**
   * Get document statistics for a patient.
   */
  getStatsByPatient: async (patientId: string): Promise<DocumentStats> => {
    const response = await apiClient.get<DocumentStats>(
      `/v1/documents/patient/${patientId}/stats`
    );
    return response.data;
  },

  /**
   * List documents linked to a task.
   */
  getByTask: async (taskId: string): Promise<DocumentListItem[]> => {
    const response = await apiClient.get<DocumentListItem[]>(`/v1/documents/task/${taskId}`);
    return response.data;
  },

  /**
   * List documents linked to a demand.
   */
  getByDemand: async (demandId: string): Promise<DocumentListItem[]> => {
    const response = await apiClient.get<DocumentListItem[]>(`/v1/documents/demand/${demandId}`);
    return response.data;
  },

  /**
   * Get document details by ID.
   */
  getById: async (id: string): Promise<DocumentDetail> => {
    const response = await apiClient.get<DocumentDetail>(`/v1/documents/${id}`);
    return response.data;
  },

  /**
   * Download document file.
   */
  download: async (id: string): Promise<Blob> => {
    const response = await apiClient.get(`/v1/documents/${id}/download`, {
      responseType: 'blob',
    });
    return response.data;
  },

  /**
   * Get document download URL.
   */
  getDownloadUrl: (id: string): string => {
    const baseUrl = import.meta.env.VITE_API_URL || '/api';
    return `${baseUrl}/v1/documents/${id}/download`;
  },

  /**
   * Get document view URL (for inline viewing).
   */
  getViewUrl: (id: string): string => {
    const baseUrl = import.meta.env.VITE_API_URL || '/api';
    return `${baseUrl}/v1/documents/${id}/view`;
  },

  /**
   * Update document metadata.
   */
  update: async (id: string, data: DocumentUpdateRequest): Promise<DocumentDetail> => {
    const response = await apiClient.put<DocumentDetail>(`/v1/documents/${id}`, data);
    return response.data;
  },

  /**
   * Link document to a task.
   */
  linkToTask: async (documentId: string, taskId: string): Promise<DocumentDetail> => {
    const response = await apiClient.post<DocumentDetail>(
      `/v1/documents/${documentId}/link/task/${taskId}`
    );
    return response.data;
  },

  /**
   * Link document to a demand.
   */
  linkToDemand: async (documentId: string, demandId: string): Promise<DocumentDetail> => {
    const response = await apiClient.post<DocumentDetail>(
      `/v1/documents/${documentId}/link/demand/${demandId}`
    );
    return response.data;
  },

  /**
   * Delete a document (soft delete).
   */
  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/v1/documents/${id}`);
  },

  /**
   * Get available document types.
   */
  getDocumentTypes: async (): Promise<DocumentTypeInfo[]> => {
    const response = await apiClient.get<DocumentTypeInfo[]>('/v1/documents/types');
    return response.data;
  },

  /**
   * Verify extraction (approve or reject).
   */
  verifyExtraction: async (id: string, request: VerifyExtractionRequest): Promise<DocumentDetail> => {
    const response = await apiClient.post<DocumentDetail>(`/v1/documents/${id}/verify`, request);
    return response.data;
  },

  /**
   * Update extracted data (correction).
   */
  updateExtractedData: async (id: string, extractedData: Record<string, unknown>): Promise<DocumentDetail> => {
    const response = await apiClient.put<DocumentDetail>(`/v1/documents/${id}/extracted-data`, extractedData);
    return response.data;
  },

  /**
   * List documents pending verification review.
   */
  getPendingReview: async (patientId: string): Promise<DocumentListItem[]> => {
    const response = await apiClient.get<DocumentListItem[]>(`/v1/documents/patient/${patientId}/pending-review`);
    return response.data;
  },

  /**
   * Trigger AI extraction for a document.
   */
  triggerExtraction: async (id: string): Promise<DocumentDetail> => {
    const response = await apiClient.post<DocumentDetail>(`/v1/documents/${id}/extract`);
    return response.data;
  },
};

/**
 * API for document management in patient portal.
 * Uses portalClient which adds X-Portal-Token header.
 */
export const portalDocumentsApi = {
  /**
   * Upload a document (as patient).
   */
  upload: async (
    file: File,
    documentType: DocumentType,
    options?: {
      title?: string;
      description?: string;
      documentDate?: string;
    }
  ): Promise<DocumentUploadResponse> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('documentType', documentType);

    if (options?.title) formData.append('title', options.title);
    if (options?.description) formData.append('description', options.description);
    if (options?.documentDate) formData.append('documentDate', options.documentDate);

    const response = await portalClient.post<DocumentUploadResponse>(
      '/v1/portal/documents',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  },

  /**
   * List my documents (as patient).
   */
  getMyDocuments: async (params?: PaginationParams): Promise<PaginatedResponse<DocumentListItem>> => {
    const response = await portalClient.get<PaginatedResponse<DocumentListItem>>(
      '/v1/portal/documents',
      { params }
    );
    return response.data;
  },

  /**
   * List all my documents (no pagination).
   */
  getAllMyDocuments: async (): Promise<DocumentListItem[]> => {
    const response = await portalClient.get<DocumentListItem[]>('/v1/portal/documents/all');
    return response.data;
  },

  /**
   * List my documents by type.
   */
  getByType: async (type: DocumentType): Promise<DocumentListItem[]> => {
    const response = await portalClient.get<DocumentListItem[]>(`/v1/portal/documents/type/${type}`);
    return response.data;
  },

  /**
   * Get my document statistics.
   */
  getStats: async (): Promise<DocumentStats> => {
    const response = await portalClient.get<DocumentStats>('/v1/portal/documents/stats');
    return response.data;
  },

  /**
   * Get document details.
   */
  getById: async (id: string): Promise<DocumentDetail> => {
    const response = await portalClient.get<DocumentDetail>(`/v1/portal/documents/${id}`);
    return response.data;
  },

  /**
   * Download document file.
   */
  download: async (id: string): Promise<Blob> => {
    const response = await portalClient.get(`/v1/portal/documents/${id}/download`, {
      responseType: 'blob',
    });
    return response.data;
  },

  /**
   * Get document download URL.
   */
  getDownloadUrl: (id: string): string => {
    const baseUrl = import.meta.env.VITE_API_URL || '/api';
    return `${baseUrl}/v1/portal/documents/${id}/download`;
  },

  /**
   * Get document view URL.
   */
  getViewUrl: (id: string): string => {
    const baseUrl = import.meta.env.VITE_API_URL || '/api';
    return `${baseUrl}/v1/portal/documents/${id}/view`;
  },

  /**
   * Get available document types for upload.
   */
  getDocumentTypes: async (): Promise<DocumentTypeInfo[]> => {
    const response = await portalClient.get<DocumentTypeInfo[]>('/v1/portal/documents/types');
    return response.data;
  },
};
